import React, { type FC, useEffect, useState } from 'react';
import { Box, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import css from './MainPageSideBar.scss';
import { useTabs } from '../../../../hooks/useTabs';
import {
  getSidebarItems,
  SideBarItemType,
} from '../../../../constants/sideBarItems';
import { DataHooks } from '../../../../constants/dataHooks';
import { getDivRefPosition } from '../../../../utils/getDivRefPosition';
import type { TemplateTab } from '../../../../types/TemplateTabs';
import classNames from 'classnames';
import { getScrollingElement } from '../../../../utils/getScrollingElementInDocs';
import { useExperiments } from '@wix/fe-essentials/react';
import { shouldDisplayHarmonyNotifications } from '../../../../utils/experiments';

type Props = {
  mainGridRef: React.RefObject<HTMLDivElement>;
};

export const MainPageSideBar: FC<Props> = ({ mainGridRef }) => {
  const { activeTabIdDebounced, onTabClick } = useTabs();
  const { t } = useTranslation();
  const [shouldScrollIntoView, setShouldScrollIntoView] =
    useState<boolean>(false);
  const shouldScrollRef = React.useRef(false);
  const { experiments } = useExperiments({ readOnly: true });
  const displayHarmonyNotifications =
    shouldDisplayHarmonyNotifications(experiments);

  const displayableSidebarItems = getSidebarItems(displayHarmonyNotifications);

  useEffect(() => {
    const scrollingElement = getScrollingElement();
    const handleScroll = () => {
      const topBoundingMainGrid = getDivRefPosition(mainGridRef, 'top');
      const shouldScroll = topBoundingMainGrid < 0;
      if (shouldScroll !== shouldScrollRef.current) {
        setShouldScrollIntoView(shouldScroll);
        shouldScrollRef.current = shouldScroll;
      }
    };
    scrollingElement.addEventListener('scroll', handleScroll);
    return () => scrollingElement.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div data-hook={DataHooks.MainPageSideBar} className={css.stickyBar}>
      <div style={{ width: '100%' }}>
        <Box direction="vertical" gap="SP4" padding="48px 32px">
          {displayableSidebarItems.map((tab) => {
            const isSelectedTab = activeTabIdDebounced === tab.id;
            const type = tab.type;
            if (type === SideBarItemType.HEADING) {
              return (
                <React.Fragment key={tab.id}>
                  <Text secondary light size="small">
                    {t(tab.sideBarItemKey)}
                  </Text>
                </React.Fragment>
              );
            } else {
              return (
                <TextButton
                  skin="dark"
                  size="medium"
                  key={tab.id}
                  className={classNames({
                    [css.selectedFilter]: isSelectedTab,
                  })}
                  weight={isSelectedTab ? 'normal' : 'thin'}
                  dataHook={DataHooks.TabItem(tab.id)}
                  onClick={() => {
                    onTabClick(
                      tab.id as TemplateTab,
                      mainGridRef,
                      shouldScrollIntoView,
                    );
                  }}
                >
                  {t(tab.sideBarItemKey)}
                </TextButton>
              );
            }
          })}
        </Box>
      </div>
    </div>
  );
};
